import { css } from '@microsoft/fast-element';
import { neutralForegroundRest, neutralInputFillHover, neutralInputFillRest, neutralStrokeFocus, neutralStrokeRest, getToken } from '../../design-tokens';
import { heightNumber, paddingNumber } from '../../styles/size';
import { display } from '../../foundation/display';

export const textFieldStyles = css`
    ${display("inline-block")} :host {
        font-family: ${getToken('typography-font-family')};
        font-size: ${getToken('typography-control-font-size')};
        line-height: ${getToken('typography-control-line-height')};

        outline: none;
        user-select: none;
    }

    .root {
        display: flex;
        flex-direction: row;
        position: relative;

        box-sizing: border-box;

        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        height: calc(${heightNumber} * 1px);

        color: ${neutralForegroundRest};
        background: ${neutralInputFillRest};
        box-shadow: 0 0 calc(${getToken('space-input-shadow')} * 1px) 0 rgba(0, 0, 0, 0.1);
    }

    :host(.label__visible) .root {
        height: calc(${heightNumber} * 1px + ${getToken('typography-type-ramp-minus-2-font-size')});
    }

    .control-container {
        flex-grow: 1;
    }

    .control {
        position: relative;
        box-sizing: border-box;
        -webkit-appearance: none;
        background: transparent;
        border: 0;
        width: 100%;
        height: 100%;
        border: none;
        padding: 0 calc(${paddingNumber} * 1px);
        font-size: inherit;
        color: ${neutralForegroundRest};
    }

    :host(.label__visible) .control {
        padding-top: calc(${getToken('typography-type-ramp-minus-2-line-height')} * ${getToken('typography-type-ramp-minus-2-font-size')});
    }

    .control:hover,
    .control:focus-visible,
    .control:disabled,
    .control:active {
        outline: none;
    }

    .label {
        position: absolute;
        top: calc(50% - ${getToken('typography-control-line-height')} * ${getToken('typography-control-font-size')} / 2);

        display: block;

        padding-left: calc(${paddingNumber} * 1px);

        color: ${neutralForegroundRest};

        user-select: none;
        cursor: text;

        transition: 200ms ease;
    }

    :host(.label__hidden) .label {
        display: none;
        visibility: hidden;
    }

    .control:focus + .label,
    .control:not(:placeholder-shown) + .label,
    :host([placeholder]) .control + .label
     {
        top: calc(${getToken('space-design-unit')} * 1px);
        font-size: ${getToken('typography-type-ramp-minus-2-font-size')};
        line-height: ${getToken('typography-type-ramp-minus-2-line-height')};
    }

    .start, .end {
        display: flex;
        align-items: center;
    }

    ::slotted([slot='start']) {
        margin-inline: calc(${getToken('space-design-unit')} * 2px);
    }

    ::slotted([slot='end']) {
        margin-inline: calc(${getToken('space-design-unit')} * 2px);
    }

    .password-toggle {
        width: 40px;
        height: 100%;
        margin-inline-end: calc(${getToken('space-design-unit')} * 2px);
    }

    :host(:hover:not(.disabled)) .root {
        background: ${neutralInputFillHover};
        border-color: ${getToken('color-primary-hover')};
    }

    :host(:active:not(.disabled)) .root {
        background: ${neutralInputFillHover};
        border-color: ${getToken('color-primary-active')};
    }

    :host(:focus-within:not(.disabled)) .root {
        border-color: ${neutralStrokeFocus};
    }

    /* TODO: Use appearance behavior for stealth text fields */
    :host(.stealth) .control {
        padding: 0;
    }

    :host(.stealth) .root,
    :host(.stealth:hover:not(.disabled)) .root,
    :host(.stealth:focus-within:not(.disabled)) .root {
        background: transparent;
        border-color: transparent;
        box-shadow: none;
    }

    :host(.disabled) .label,
    :host(.readonly) .label,
    :host(.readonly) .control,
    :host(.disabled) .control {
        cursor: not-allowed;
    }

    :host(.disabled) {
        opacity: ${getToken('color-disabled-opacity')};
    }

    :host([disabled]) .control {
        border-color: ${neutralStrokeRest};
    }

    :host(.error) .root {
        border-color: ${getToken('color-error')};
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    input[type=number] {
        -moz-appearance: textfield;
    }
`;
